import { IconNames, Text } from '@dixa/lib-shared-habla';
import { boundMethod } from 'autobind-decorator';
import { Sentry, SentryTeam } from 'lib-shared-sentry';
import { v4 as uuid } from 'uuid';

import { FlowNodeTypes } from 'components/powerFlowShared/components/types';
import {
  FlowNode,
  flowNodeColor,
  InternalPortT,
  NodeT,
  NodeTypeComponent,
  RegisterPortT,
} from 'fable/flowEditor';

import { formatPortsHelper } from '../../../powerFlowShared/helpers/nodePortFormatter';
import ActionModal from '../../containers/modals/widgetcustomization';
import { PowerChatFlowExtraDataT } from '../PowerChatFlow';
import {
  LocalizationLabelsT,
  LocalizationsT,
  PositionLabelsT,
  Positions,
  Views,
  ViewTypes,
} from '../types';

export type PositionT = {
  position: Positions;
  view?: Views;
};

type MinimizedTypeT = {
  html_text_offline: string;
  html_text_online: string;
};

export type MinimizedT = {
  minimized: MinimizedTypeT;
};

export type ThemeT = {
  color_darken: string;
  color_lighten: string;
  color: string;
  text_color: string;
};

export type LocalizationsTypeT = {
  value: LocalizationsT;
  label: LocalizationLabelsT;
};

export type LocalizationsPayloadT = Record<string, MinimizedT>;

export type PositionOptionT = {
  value: Positions;
  label: PositionLabelsT;
};

export type ViewT = Record<ViewTypes, PositionT>;

export type FlowChatNodeWidgetCustomizationPayloadT = {
  loaded?: boolean;
  localizations: LocalizationsPayloadT;
  medium: ViewT;
  nextNodeId: string;
  small: ViewT;
  theme: ThemeT;
};

type FlowChatNodeWidgetCustomizationTypeT = FlowNodeTypes.WIDGET_CUSTOMIZATION;
export type FlowChatNodeWidgetCustomizationT = NodeT<
  FlowChatNodeWidgetCustomizationTypeT,
  FlowChatNodeWidgetCustomizationPayloadT
>;

const portTranslator = (
  node: FlowChatNodeWidgetCustomizationT,
): RegisterPortT[] => {
  return [
    {
      id: uuid(),
      type: 'out',
      label: 'arrow',
      connection: node.payload.nextNodeId || uuid(),
    },
  ];
};

export class WidgetCustomizationNode extends NodeTypeComponent<
  FlowChatNodeWidgetCustomizationT,
  PowerChatFlowExtraDataT
>() {
  static icon: IconNames = 'widget-customize';
  static label = 'Customization';
  static color = flowNodeColor.blue;
  static height = FlowNode.dimensions.h + 16;
  static translatePayload = formatPortsHelper(portTranslator);
  static ActionModal = ActionModal;
  static actionTrigger = { type: FlowNodeTypes.WIDGET_CUSTOMIZATION };
  static updateConnection = (
    node: FlowChatNodeWidgetCustomizationT,
    _port: InternalPortT,
    newConnection: string,
  ): FlowChatNodeWidgetCustomizationT => {
    const { payload } = node;
    return {
      ...node,
      payload: {
        ...payload,
        nextNodeId: newConnection,
      },
    };
  };

  @boundMethod
  triggerEdit(): void {
    const { onEdit, node } = this.props;
    onEdit(node, node.nodeType);
  }

  render() {
    const { node } = this.props;
    const { payload } = node;
    const { theme, medium, small } = payload;
    const mobilePosition = small?.minimized_view.position.replace('_', ' ');
    const desktopPosition = medium?.minimized_view.position.replace('_', ' ');

    if (!small || !medium)
      Sentry.captureMessage('Invalid Widgetcustomization', {
        contexts: {
          node: {
            ...node,
            payload: JSON.stringify(payload),
          },
        },
        tags: { team: SentryTeam.Routing },
      });

    const pillStyle = {
      display: 'inline-block',
      backgroundColor: theme.color,
      width: '30px',
      height: '12px',
      borderRadius: '6px',
      margin: '0 2px -1px',
    };

    return (
      <FlowNode
        color={WidgetCustomizationNode.color}
        icon={WidgetCustomizationNode.icon}
        label={WidgetCustomizationNode.label}
        text={
          <Text.Paragraph clamp={2}>
            Theme color: <span style={pillStyle}></span>
            {mobilePosition && desktopPosition && (
              <>
                , Position:
                <strong>
                  {mobilePosition} on mobile and {desktopPosition} on desktop
                </strong>
              </>
            )}
          </Text.Paragraph>
        }
        onEdit={this.triggerEdit}
      />
    );
  }
}
